import Backbone from 'backbone'
import _ from 'lodash'
import $ from 'jquery'
import template from './y-axis-range-config.hbs'
import MarkedField from 'scoreboard/components/marked-field'
import numericEvaluator from 'scoreboard/components/evaluators/numeric-evaluator'
import numericValidator from 'scoreboard/components/validators/numeric-validator'
import FormView from 'scoreboard/components/form-view'
import formHelper from 'scoreboard/components/form-helper'
import cleanObject from 'scoreboard/components/clean-object'
import constants from 'scoreboard/components/constants'
import i18n from 'scoreboard/components/i18n'
import Dataset from 'scoreboard/datasets/model/dataset'
import DataType from 'scoreboard/datasets/model/data-type'
import isRtl from '../../../../components/is-rtl'
const { NUMBER } = DataType

var YAxisRangeConfig = Backbone.View.extend({
	tagName: 'form',
	className: 'y-axis-range-config',

	initialize: function (opt) {
		this.data = opt.data
	},

	render: function () {
		this.$el.html(template(this.context()))

		var axisInfo = axisDataTypeInfo(this.model, this.data)

		this._markedFields = this.$('input').toArray().map(input => {
			let inputName = $(input).attr('name')

			var axis = inputName.indexOf('left') === 0 ? 'left' : 'right'
			var info = axisInfo[axis] || {}

			// #22899 set input value before creating MarkedField so it handles
			// percentage values correctly (ie displays "1" as "100%")
			let val = this.model.get(inputName)
			input.value = !_.isNaN(parseFloat(val)) ? val : ''

			return new MarkedField({
				el: input,
				evaluator: numericEvaluator,
				validator: numericValidator,
				dataType: info.dataType,
				currencyCode: info.currencyCode,
				unit: info.unit
			}).render()
		})

		_.forEach(['left', 'right'], axis => {
			this.$(`.${axis}-axis-inputs, .${axis}-axis-label`).toggleClass('is-not-displayed', !axisInfo[axis])
		})

		this._formView = new FormView({
			el: this.el,
			submit: () => this.save(),
			test: () => this.test()
		})
		this._formView.start()

		return this
	},

	context: function () {
		return {
			apiLeftAxis: isRtl() ? 'right' : 'left',
			apiRightAxis: isRtl() ? 'left' : 'right'
		}
	},

	save: function () {
		this.model.set(formHelper.readFromDom(this.el))
		this.trigger('save')
	},

	test: function () {
		let data = formHelper.readFromDom(this.el)

		return testMinLessThanMax(data, 'left')
			|| testMinLessThanMax(data, 'right')
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap(this._markedFields, 'remove')
		_.invokeMap([this._formView], 'remove')
	}
})

/**
 * Returns an error message if the min value is not less than the max value.
 *
 * @param data - an object containing properties like leftAxisMin, rightAxisMax, etc
 * @param leftOrRight - 'left' or 'right' to specify the axis
 */
function testMinLessThanMax (data, leftOrRight) {
	let min = data[leftOrRight + 'AxisMin']
	let max = data[leftOrRight + 'AxisMax']

	if (!_.isNumber(min) || !_.isNumber(max)) {
		return
	}

	if (min >= max) {
		return i18n.get('charts.axisRangeInvalid')
	}
}

/**
 * Returns appropriate dataType, currencyCode, and unit for the chart's axes. Example:
 * {
 *   left: { dataType: 'number' },
 *   right: {
 *   	dataType: 'currency',
 *      currencyCode: 'USD'
 *   }
 * }
 * @param {ChartData} data
 */
function axisDataTypeInfo (chart, data) {
	let series = chart.getFeatures({ type: 'serie'})
	var featuresByAxis = _.groupBy(series, f => f.get('axis'))
	var dataTypeByAxis = ['left', 'right'].reduce((result, axis) => {
		var info
		let features = featuresByAxis[axis] ?? []
		let featuresInfo = features.map(feature => {
			let featuresSource = feature.get('source')
			if (featuresSource.domainType === 'chart-placeholder') {
				// #40106 this chart still has placeholder data, so just use the
				// default info
				return DEFAULT_DATATYPE_INFO
			}

			let sectionInfo = sectionInfoByDomainType[featuresSource.domainType]
			let domainId = featuresSource[sectionInfo.idProperty] ||
				featuresSource?.aggregation?.startingDatasetId

			return getDataTypeInfo(data, sectionInfo.section, domainId, featuresSource)
		})

		// Can only use specified data type info if all series on the axis have the same settings. If not, just use
		// the default "number"
		info = _.uniqWith(featuresInfo, _.isEqual).length != 1
			? DEFAULT_DATATYPE_INFO
			: featuresInfo[0]

		result[axis] = cleanObject(info)
		return result
	}, {})

	return dataTypeByAxis
}

/**
 * @param {ChartData} data
 */
function getDataTypeInfo (data, section, domainObjectId, model) {
	let domainObject = data.domainObject(section, domainObjectId)

	if (section === 'scorecards') {
		if (model.dataProperty == 'score') {
			return { dataType: 'score' }
		} else {
			return _.pick(domainObject, dataTypeProps)
		}
	} else if (section === 'initiatives') {
		return {
			dataType: constants.initiativeCalculationTypes[model.dataProperty].dataType,
			currencyCode: domainObject.currencyCode
		}
	} else if (section === 'datasets') {
		if (!domainObjectId) return DEFAULT_DATATYPE_INFO

		let dataset = data.datasetModel(domainObjectId)
		let aggregationField = dataset.combinedFieldsById[_.get(model,
			'aggregation.fieldId')]

		return aggregationField ? _.pick(aggregationField, dataTypeProps)
			: DEFAULT_DATATYPE_INFO
	}
}

const sectionInfoByDomainType = {
	'scorecard-item': {
		section: 'scorecards',
		idProperty: 'scorecardItemId'
	},
	'initiative': {
		section: 'initiatives',
		idProperty: 'initiativeId'
	},
	'dataset': {
		section: 'datasets',
		idProperty: 'datasetId'
	}
}

const dataTypeProps = ['dataType', 'currencyCode', 'unit']

const DEFAULT_DATATYPE_INFO = { dataType: NUMBER }

export default YAxisRangeConfig

// for unit tests
export { axisDataTypeInfo }
export { getDataTypeInfo }
export { DEFAULT_DATATYPE_INFO }

YAxisRangeConfig.__filename__ = 'dashboards/widgets/chart/config/y-axis-range-config.js'
